import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { parse as parseYaml } from 'yaml';
import SearchBar from './SearchBar';
import { PostListSkeleton } from './Skeleton';

// Simple frontmatter parser using modern yaml package (no esprima dependency)
const parseFrontmatter = content => {
  const frontmatterRegex = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);
  if (!match) {
    return { attributes: {}, body: content };
  }
  try {
    const attributes = parseYaml(match[1]) || {};
    return { attributes, body: match[2] };
  } catch {
    return { attributes: {}, body: content };
  }
};

// Calculate reading time (average 200 words per minute)
const calculateReadingTime = content => {
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return minutes;
};

// Get reading time color class based on duration
const getReadingTimeClass = minutes => {
  if (minutes <= 5) return 'reading-time-quick';
  if (minutes <= 10) return 'reading-time-medium';
  return 'reading-time-long';
};

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [readingTimeFilter, setReadingTimeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lunrLoaded, setLunrLoaded] = useState(false);
  const searchIndexRef = useRef(null);
  const lunrRef = useRef(null);
  const postsRef = useRef([]);

  // Build Lunr index when Lunr is loaded and posts are available
  const buildSearchIndex = useCallback((postsData, lunrModule) => {
    if (!lunrModule || !postsData.length) return null;
    return lunrModule(function () {
      this.field('title');
      this.field('content');
      this.field('excerpt');
      this.field('tags');

      postsData.forEach((post, idx) => {
        this.add({
          id: idx,
          title: post.title,
          content: post.content,
          excerpt: post.excerpt,
          tags: post.tags.join(', '),
        });
      });
    });
  }, []);

  // Lazy load Lunr when user starts searching
  useEffect(() => {
    if (searchTerm && !lunrLoaded && !lunrRef.current) {
      import('lunr').then(module => {
        lunrRef.current = module.default;
        setLunrLoaded(true);
        // Build index with existing posts
        if (postsRef.current.length > 0) {
          searchIndexRef.current = buildSearchIndex(postsRef.current, module.default);
        }
      });
    }
  }, [searchTerm, lunrLoaded, buildSearchIndex]);

  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      const context = require.context('../blogPosts', false, /\.md$/);
      const postsData = await Promise.all(
        context.keys().map(async key => {
          const content = await import(`../blogPosts/${key.replace('./', '')}`);
          const meta = parseFrontmatter(content.default);
          return {
            ...meta.attributes,
            content: meta.body,
            slug: key.replace('./', '').replace('.md', ''),
            tags: meta.attributes.tags || [],
            date: meta.attributes.date,
            readingTime: calculateReadingTime(meta.body),
          };
        })
      );

      // Sort by date (newest first) but don't display dates
      postsData.sort((a, b) => new Date(b.date) - new Date(a.date));

      setPosts(postsData);
      postsRef.current = postsData;

      // Build search index if Lunr is already loaded
      if (lunrRef.current) {
        searchIndexRef.current = buildSearchIndex(postsData, lunrRef.current);
      }

      setIsLoading(false);
    };

    fetchPosts();
  }, [buildSearchIndex]);

  // Get all unique tags with counts, sorted by frequency
  const { primaryTags, secondaryTags } = useMemo(() => {
    const tagCounts = {};
    posts.forEach(post =>
      post.tags.forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      })
    );

    // Sort by count (descending), then alphabetically
    const sortedTags = Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([tag]) => tag);

    // Show top 6 tags as primary, rest as secondary
    return {
      primaryTags: sortedTags.slice(0, 6),
      secondaryTags: sortedTags.slice(6),
    };
  }, [posts]);

  const [showAllTags, setShowAllTags] = useState(false);

  // Filter by search term, tag, reading time, and date
  const filteredPosts = useMemo(() => {
    let result = posts;

    // Filter by tag first
    if (selectedTag) {
      result = result.filter(post => post.tags.includes(selectedTag));
    }

    // Filter by reading time
    if (readingTimeFilter !== 'all') {
      result = result.filter(post => {
        switch (readingTimeFilter) {
          case 'quick':
            return post.readingTime <= 5;
          case 'medium':
            return post.readingTime > 5 && post.readingTime <= 10;
          case 'long':
            return post.readingTime > 10;
          default:
            return true;
        }
      });
    }

    // Filter by date
    if (dateFilter !== 'all') {
      const now = new Date();
      result = result.filter(post => {
        const postDate = new Date(post.date);
        const daysDiff = (now - postDate) / (1000 * 60 * 60 * 24);
        switch (dateFilter) {
          case 'week':
            return daysDiff <= 7;
          case 'month':
            return daysDiff <= 30;
          case 'quarter':
            return daysDiff <= 90;
          case 'year':
            return daysDiff <= 365;
          default:
            return true;
        }
      });
    }

    // Then filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      // Use Lunr if available, otherwise fall back to simple search
      if (searchIndexRef.current) {
        try {
          const searchResults = searchIndexRef.current.search(`${searchTerm}*`);
          const matchedSlugs = new Set(
            searchResults.map(({ ref }) => posts[ref]?.slug).filter(Boolean)
          );
          result = result.filter(post => matchedSlugs.has(post.slug));
        } catch {
          // If Lunr search fails, fall back to simple search
          result = result.filter(
            post =>
              post.title.toLowerCase().includes(term) ||
              post.excerpt?.toLowerCase().includes(term) ||
              post.tags.some(tag => tag.toLowerCase().includes(term))
          );
        }
      } else {
        // Simple search while Lunr is loading
        result = result.filter(
          post =>
            post.title.toLowerCase().includes(term) ||
            post.excerpt?.toLowerCase().includes(term) ||
            post.tags.some(tag => tag.toLowerCase().includes(term))
        );
      }
    }

    return result;
  }, [posts, searchTerm, selectedTag, readingTimeFilter, dateFilter]);

  // Check if any filters are active
  const hasActiveFilters = readingTimeFilter !== 'all' || dateFilter !== 'all';

  // Clear all filters
  const clearFilters = () => {
    setReadingTimeFilter('all');
    setDateFilter('all');
    setSelectedTag(null);
    setSearchTerm('');
  };

  return (
    <div className="blog-list">
      {isLoading ? (
        <PostListSkeleton count={5} />
      ) : (
        <>
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

          {/* Advanced filters toggle */}
          <div className="filters-toggle">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`filters-toggle-btn ${showFilters || hasActiveFilters ? 'active' : ''}`}
              aria-expanded={showFilters}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              </svg>
              Filters
              {hasActiveFilters && <span className="filter-badge">Active</span>}
            </button>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="clear-filters-btn">
                Clear all
              </button>
            )}
          </div>

          {/* Advanced filters */}
          {showFilters && (
            <div className="advanced-filters">
              <div className="filter-group">
                <label className="filter-label">Reading time</label>
                <div className="filter-options">
                  <button
                    onClick={() => setReadingTimeFilter('all')}
                    className={`filter-btn ${readingTimeFilter === 'all' ? 'active' : ''}`}
                  >
                    Any
                  </button>
                  <button
                    onClick={() => setReadingTimeFilter('quick')}
                    className={`filter-btn ${readingTimeFilter === 'quick' ? 'active' : ''}`}
                  >
                    Quick (&lt;5 min)
                  </button>
                  <button
                    onClick={() => setReadingTimeFilter('medium')}
                    className={`filter-btn ${readingTimeFilter === 'medium' ? 'active' : ''}`}
                  >
                    Medium (5-10 min)
                  </button>
                  <button
                    onClick={() => setReadingTimeFilter('long')}
                    className={`filter-btn ${readingTimeFilter === 'long' ? 'active' : ''}`}
                  >
                    Long (&gt;10 min)
                  </button>
                </div>
              </div>
              <div className="filter-group">
                <label className="filter-label">Published</label>
                <div className="filter-options">
                  <button
                    onClick={() => setDateFilter('all')}
                    className={`filter-btn ${dateFilter === 'all' ? 'active' : ''}`}
                  >
                    All time
                  </button>
                  <button
                    onClick={() => setDateFilter('week')}
                    className={`filter-btn ${dateFilter === 'week' ? 'active' : ''}`}
                  >
                    Past week
                  </button>
                  <button
                    onClick={() => setDateFilter('month')}
                    className={`filter-btn ${dateFilter === 'month' ? 'active' : ''}`}
                  >
                    Past month
                  </button>
                  <button
                    onClick={() => setDateFilter('quarter')}
                    className={`filter-btn ${dateFilter === 'quarter' ? 'active' : ''}`}
                  >
                    Past 3 months
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tag filters */}
          {primaryTags.length > 0 && (
            <div className="tag-filters">
              <div className="tag-filters-row">
                <button
                  onClick={() => setSelectedTag(null)}
                  className={`tag-button ${!selectedTag ? 'active' : ''}`}
                >
                  All
                </button>
                {primaryTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(tag)}
                    className={`tag-button ${selectedTag === tag ? 'active' : ''}`}
                  >
                    {tag}
                  </button>
                ))}
                {secondaryTags.length > 0 && (
                  <button
                    onClick={() => setShowAllTags(!showAllTags)}
                    className="tag-button tag-button-more"
                  >
                    {showAllTags ? 'Less' : `+${secondaryTags.length} more`}
                  </button>
                )}
              </div>
              {showAllTags && secondaryTags.length > 0 && (
                <div className="tag-filters-expanded">
                  {secondaryTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => setSelectedTag(tag)}
                      className={`tag-button ${selectedTag === tag ? 'active' : ''}`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <ul className="posts-list">
            {filteredPosts.map(post => (
              <li key={post.slug} className="post-item">
                <Link to={`/blog/${post.slug}`}>
                  {post.series && (
                    <span className="post-series-badge">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                      </svg>
                      Part {post.series.part} of "{post.series.name}"
                    </span>
                  )}
                  <h3 className="post-title">{post.title}</h3>
                  <p className="post-excerpt">{post.excerpt}</p>
                  <p className="post-meta">
                    <span className={`post-reading-time ${getReadingTimeClass(post.readingTime)}`}>
                      <svg
                        className="reading-time-icon"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      {post.readingTime} min read
                    </span>
                    {post.tags.length > 0 && (
                      <span className="post-tags">
                        {post.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="tag">
                            {tag}
                          </span>
                        ))}
                      </span>
                    )}
                  </p>
                </Link>
              </li>
            ))}
          </ul>

          {filteredPosts.length === 0 && (
            <p className="no-results">No articles found matching your criteria.</p>
          )}

          {filteredPosts.length > 0 && (
            <div className="archive-link">
              <Link to="/archive">View all articles →</Link>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BlogList;
